import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useDocument, useDocumentStatus, useExtractedKpis, useSavedKpis } from '../hooks/useDocSenseData';
import ProcessingTracker from './ProcessingTracker';
import KpiReviewTable from './KpiReviewTable';

/**
 * Props:
 *   documentId           – required
 *   onBack               – required
 *   financialYearOptions – [{ value, label }]
 *   sources              – raw sources array for name resolution
 */
const DocumentDetailPage = ({ documentId, onBack, financialYearOptions = [], sources = [] }) => {
  const { data: doc, isLoading: docLoading } = useDocument(documentId);

  const isProcessing = doc && !['COMPLETED', 'FAILED'].includes(doc.status);
  const { status: polledStatus } = useDocumentStatus(documentId, isProcessing);

  const [displayStatus, setDisplayStatus] = useState(null);
  const currentStatus = displayStatus || doc?.status;
  const isCompleted = currentStatus === 'COMPLETED';

  const {
    data: extractedKpis,
    isLoading: extractedLoading,
    refetch: refetchExtracted,
  } = useExtractedKpis(isCompleted ? documentId : null);

  const {
    data: savedKpis,
    isLoading: savedLoading,
    refetch: refetchSaved,
  } = useSavedKpis(documentId);

  const hasSavedKpis = !savedLoading && savedKpis.length > 0;
  const kpisNotSavedYet = isCompleted && !savedLoading && savedKpis.length === 0;

  // Always show review table once processing is complete
  const showReviewTable = isCompleted;

  useEffect(() => {
    if (doc?.status) setDisplayStatus(doc.status);
  }, [doc?.status]);

  useEffect(() => {
    if (polledStatus?.status) {
      setDisplayStatus(polledStatus.status);
      if (polledStatus.status === 'COMPLETED') refetchExtracted();
    }
  }, [polledStatus]);

  const handleKpisSaved = () => {
    refetchSaved();
    refetchExtracted(); // refresh extracted list so Not Saved tab count updates
  };

  // ── Name resolvers ────────────────────────────────────────────────────────
  const resolveFYName = (fyId) =>
    financialYearOptions.find(f => String(f.value) === String(fyId))?.label || fyId || null;

  const resolveLocationName = (sourceId) => {
    const src = sources.find(s => String(s.id) === String(sourceId));
    if (!src) return sourceId || null;
    return src.unitCode || `${src.location?.city} - ${src.location?.state}`;
  };

  const resolveSubLocationName = (sourceId, subLocId) => {
    const src = sources.find(s => String(s.id) === String(sourceId));
    if (!src) return subLocId || null;
    const sub = (src.subLocation || []).find(s => String(s.id) === String(subLocId));
    return sub?.subLocation || sub?.name || subLocId || null;
  };

  // ─────────────────────────────────────────────────────────────────────────

  const formatDate = (d) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    } catch { return d; }
  };

  const getFileIcon = (fileName) => {
    if (!fileName) return '📄';
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return '📄';
    if (['xlsx', 'xls'].includes(ext)) return '📊';
    if (['doc', 'docx'].includes(ext)) return '📝';
    return '📄';
  };

  if (docLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 20px', gap: 16, color: '#6b8794' }}>
        <Spinner animation="border" style={{ color: '#3f88a5' }} />
        <span>Loading document…</span>
      </div>
    );
  }

  const fileName = doc?.fileName || doc?.documentProcessing?.rawFileMetadata?.fileName || 'Unknown Document';

  // Resolved names
  const fyLabel = doc?.financialYearId ? resolveFYName(doc.financialYearId) : null;
  const locLabel = doc?.sourceId ? (doc.locationName || resolveLocationName(doc.sourceId)) : null;
  const subLocLabel = doc?.subLocationId
    ? (doc.subLocationName || resolveSubLocationName(doc.sourceId, doc.subLocationId))
    : null;

  return (
    <div>
      {/* Back */}
      <button
        onClick={onBack}
        style={{
          background: 'none', border: 'none', color: '#3f88a5', fontWeight: 500,
          cursor: 'pointer', padding: 0, marginBottom: 16,
          display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.875rem',
        }}
        onMouseEnter={e => e.currentTarget.style.opacity = 0.7}
        onMouseLeave={e => e.currentTarget.style.opacity = 1}
      >
        <i className="fa fa-arrow-left" style={{ fontSize: '0.8rem' }} />
        Back to Documents
      </button>

      {/* Document header card */}
      <div style={{
        background: 'white', border: '1.5px solid #e2eaf0', borderRadius: 12,
        padding: '20px 24px', marginBottom: 20,
        display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap',
      }}>
        <div style={{
          width: 48, height: 48, borderRadius: 10, background: '#e8f4f8',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', flexShrink: 0,
        }}>
          {getFileIcon(fileName)}
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <h5 style={{ margin: 0, fontWeight: 700, color: '#1a2e38', fontSize: '1rem' }}>{fileName}</h5>
          <div style={{ fontSize: '0.82rem', color: '#6b8794', marginTop: 4 }}>
            <i className="fa fa-clock-o me-1" />Uploaded {formatDate(doc?.createdAt)}
          </div>
        </div>
        {(extractedKpis.length > 0 || hasSavedKpis) && (
          <div style={{ display: 'flex', gap: 20 }}>
            {extractedKpis.length > 0 && <StatPill label="Extracted" value={extractedKpis.length} color="#3f88a5" />}
            {hasSavedKpis && <StatPill label="Saved" value={savedKpis.length} color="#27ae60" />}
          </div>
        )}
        {/* Preview / Download */}
        {doc?.fileUrl && (
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <a
              href={doc.fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: '1.5px solid #e2eaf0', color: '#6b8794', borderRadius: 7, padding: '6px 12px', fontSize: '0.82rem', fontWeight: 500, textDecoration: 'none', transition: 'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#3f88a5'; e.currentTarget.style.color = '#3f88a5'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2eaf0'; e.currentTarget.style.color = '#6b8794'; }}
              title="Preview file"
            >
              <i className="fa fa-eye" style={{ fontSize: '0.8rem' }} /> Preview
            </a>
            <a
              href={doc.fileUrl}
              download={fileName}
              style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#3f88a5', border: 'none', color: 'white', borderRadius: 7, padding: '6px 12px', fontSize: '0.82rem', fontWeight: 500, textDecoration: 'none', transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background = '#2d6a84'}
              onMouseLeave={e => e.currentTarget.style.background = '#3f88a5'}
              title="Download file"
            >
              <i className="fa fa-download" style={{ fontSize: '0.8rem' }} /> Download
            </a>
          </div>
        )}
      </div>

      {/* Processing Tracker */}
      <ProcessingTracker
        status={currentStatus}
        errorMessage={doc?.errorMessage || polledStatus?.errorMessage}
      />

      {/* ── FY / Location / SubLocation — shown after tracker ── */}
      {(fyLabel || locLabel || subLocLabel) && (
        <div style={{
          display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20,
        }}>
          {fyLabel && <MetadataChip icon="fa-calendar" label="Financial Year" value={fyLabel} />}
          {locLabel && <MetadataChip icon="fa-map-marker" label="Location" value={locLabel} />}
          {subLocLabel && <MetadataChip icon="fa-map-pin" label="Sub Location" value={subLocLabel} />}
        </div>
      )}

      {/* KPIs not saved yet — compact inline notice (no button, table is already shown) */}
      {kpisNotSavedYet && !extractedLoading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(240,165,0,0.06)', border: '1px solid rgba(240,165,0,0.3)', borderRadius: 8, padding: '10px 16px', marginBottom: 16 }}>
          <span style={{ fontSize: '1rem' }}>⚠️</span>
          <span style={{ fontSize: '0.83rem', color: '#8a5a00' }}>
            {extractedKpis.length > 0
              ? `${extractedKpis.length} KPI${extractedKpis.length !== 1 ? 's' : ''} extracted — review the table below and save.`
              : 'No KPIs were extracted. You can still save manually from the table below.'}
          </span>
        </div>
      )}

      {/* KPI review table — always visible once completed */}
      {showReviewTable && (
        <KpiReviewTable
          documentId={documentId}
          extractedKpis={extractedKpis}
          extractedLoading={extractedLoading}
          savedKpis={savedKpis}
          savedLoading={savedLoading}
          onSaved={handleKpisSaved}
          onCancelReview={null}
          refetchExtracted={refetchExtracted}
        />
      )}

      {/* Still processing */}
      {!isCompleted && currentStatus !== 'FAILED' && (
        <div style={{
          background: 'white', border: '1.5px solid #e2eaf0', borderRadius: 12,
          padding: '40px 24px', textAlign: 'center', color: '#6b8794',
        }}>
          <Spinner animation="border" style={{ color: '#3f88a5', marginBottom: 12 }} />
          <p style={{ margin: 0, fontSize: '0.9rem' }}>KPIs will appear here once extraction is complete.</p>
        </div>
      )}
    </div>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const StatPill = ({ label, value, color }) => (
  <div style={{ textAlign: 'center' }}>
    <div style={{ fontSize: '1.3rem', fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
    <div style={{ fontSize: '0.72rem', color: '#6b8794', fontWeight: 500, marginTop: 2 }}>{label}</div>
  </div>
);

const MetadataChip = ({ icon, label, value }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 10,
    background: 'white', border: '1.5px solid #e2eaf0', borderRadius: 8,
    padding: '8px 14px',
  }}>
    <i className={`fa ${icon}`} style={{ color: '#3f88a5', fontSize: '0.85rem' }} />
    <div>
      <div style={{ fontSize: '0.68rem', color: '#9ab4bf', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 1 }}>{label}</div>
      <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#1a2e38' }}>{value}</div>
    </div>
  </div>
);

export default DocumentDetailPage;